import Popup from '@shared/components/Popup/Popup';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import ModalFooter from 'components/ModalFooter';
import useMeetingPermissionsForRoom from 'hooks/useMeetingPermissionsForRoom/useMeetingPermissionsForRoom';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { isPrivateRoom } from 'utils/matrixUtils';
import testingConstants from 'utils/testingConstants';

import AliasField from './AliasField/AliasField';
import PrivateRoomToggleField from './PrivateRoomToggleField';
import RoomNameField from './RoomNameField';
import SchedulingToggleField from './SchedulingToggleField/SchedulingToggleField';
import useCreateRoomStep from './useCreateRoomStep';
import WaitingListToggleField from './WaitingListToggleField';
import useCreateNewRoomDataContext from '../CreateNewRoomDataProvider/useCreateNewRoomDataContext';
import { AliasStatusEnum, RoomFormType } from '../CreateNewRoomDataProvider/utils';
import ProgressBar from '../ProgressBar';
import { CreateNewRoomStepsEnum } from '../utils';

const currentStepFields: (keyof RoomFormType)[] = ['roomName', 'roomAlias', 'isWaitingListEnabled'];
const scheduleFields: (keyof RoomFormType)[] = [
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'description',
  'timeZone',
];

interface CreateRoomStepModalProps {
  onGoBack?: () => void;
  hideScheduling?: boolean;
}

const CreateRoomStepModal = ({ onGoBack, hideScheduling }: CreateRoomStepModalProps) => {
  const { t } = useTranslation();
  const { workspaceId, workspacePermissions } = useWorkspacesContext();
  const { handleFormSubmit, isLoading } = useCreateRoomStep();
  const {
    handleExit,
    setCurrentStep,
    setHasToReturnToReview,
    hasToReturnToReview,
    isSchedulingAvailable,
    isScheduling,
    aliasStatus,
    roomToEdit,
    roomForm: {
      watch,
      formState: { dirtyFields, errors },
    },
  } = useCreateNewRoomDataContext();
  const meetingPermissions = useMeetingPermissionsForRoom(roomToEdit);

  const [watchStartDate, watchStartTime, watchEndDate, watchEndTime] = watch([
    'startDate',
    'startTime',
    'endDate',
    'endTime',
  ]);

  const isPrivateMeeting = roomToEdit ? isPrivateRoom(roomToEdit) : false;
  const canEditMeetingDetailsOnEdit = !!roomToEdit && meetingPermissions.canEditMeetingDetails;
  const showNameAndAlias = !roomToEdit || canEditMeetingDetailsOnEdit;

  // NOTE: hide waiting list toggle if we edit a scheduled room as there is no way to unschedule it
  const isWaitingListToggleVisibleOnCreate = !roomToEdit && workspacePermissions.createWaitingListMeeting;
  const isWaitingListToggleVisibleOnEdit =
    !!roomToEdit &&
    !isScheduling &&
    meetingPermissions.canToggleWaitingList &&
    (!isPrivateMeeting || meetingPermissions.canEditMeetingDetails);
  const isWaitingListToggleVisible = isWaitingListToggleVisibleOnCreate || isWaitingListToggleVisibleOnEdit;

  // BUG-031 — on create, the private-room toggle is shown only in personal context (no active
  // workspace); inside a workspace it is hidden. The `createPrivateMeeting` permission is still
  // respected for personal context. The edit branch is unchanged.
  const isPrivateRoomToggleVisible = roomToEdit
    ? !isScheduling && canEditMeetingDetailsOnEdit
    : !workspaceId && workspacePermissions.createPrivateMeeting;

  const isSchedulingToggleVisible = roomToEdit
    ? !hideScheduling && isSchedulingAvailable && canEditMeetingDetailsOnEdit
    : !hideScheduling && isSchedulingAvailable && workspacePermissions.createScheduledMeeting;

  const activeCurrentStepFields: (keyof RoomFormType)[] = showNameAndAlias
    ? currentStepFields
    : ['isWaitingListEnabled'];

  const isScheduleFieldMissing = !watchStartDate || !watchStartTime || !watchEndDate || !watchEndTime;
  const isScheduledIncorrectly = isSchedulingToggleVisible && isScheduling && isScheduleFieldMissing;
  const isFormChanged = !!Object.keys(dirtyFields).length;
  const hasErrorsInCurrentStep = activeCurrentStepFields.some((field) => !!errors[field]);
  const hasErrorsInScheduledFields =
    canEditMeetingDetailsOnEdit && isScheduling && scheduleFields.some((field) => !!errors[field]);
  const hasErrors = hasErrorsInCurrentStep || hasErrorsInScheduledFields;
  const isSuccessDisabled =
    hasErrors ||
    (!isFormChanged && !isScheduling) ||
    (showNameAndAlias && aliasStatus !== AliasStatusEnum.AVAILABLE) ||
    isScheduledIncorrectly;

  const modalTitle = roomToEdit ? t('edit_room') : t('create_room');
  const submitButtonText = roomToEdit ? t('save') : t('create_room');
  const [handleCancel, cancelText] = onGoBack ? [onGoBack, t('back')] : [handleExit, t('cancel')];
  const handleGoNext = () => {
    if (!hasToReturnToReview) return setCurrentStep(CreateNewRoomStepsEnum.INVITE_PARTICIPANTS);

    setHasToReturnToReview(false);
    setCurrentStep(CreateNewRoomStepsEnum.REVIEW_ROOM);
  };
  const [primaryButtonText, handleSubmit] =
    isScheduling && isSchedulingAvailable && canEditMeetingDetailsOnEdit
      ? [hasToReturnToReview ? t('save') : t('next'), handleGoNext]
      : [submitButtonText, handleFormSubmit];

  return (
    <Popup isVisible>
      <Popup.FullScreenContainer className="md:w-[512px]">
        <Popup.Header
          className={cx('flex-col justify-between !mx-0 px-4 pt-4 !shadow-none', {
            '!border-none': isScheduling && isSchedulingAvailable,
          })}
        >
          <div className="flex w-full justify-between">
            {modalTitle}
            <Popup.CloseIcon closePopup={handleExit} className="!static" />
          </div>
          <ProgressBar />
        </Popup.Header>
        <div className="flex flex-col grow overflow-y-auto gap-4 p-4" data-testid={testingConstants.createRoomModal}>
          {showNameAndAlias && (
            <>
              <RoomNameField shouldSyncAlias />
              <AliasField />
            </>
          )}
          {isWaitingListToggleVisible && <WaitingListToggleField />}
          {isPrivateRoomToggleVisible && <PrivateRoomToggleField />}
          {isSchedulingToggleVisible && <SchedulingToggleField />}
        </div>
        <ModalFooter
          onCloseClick={handleCancel}
          onSubmit={handleSubmit}
          successButtonText={primaryButtonText}
          cancelText={cancelText}
          successDisabled={isSuccessDisabled}
          isLoading={isLoading}
          isCancelButtonHidden={hasToReturnToReview}
        />
      </Popup.FullScreenContainer>
    </Popup>
  );
};

export default CreateRoomStepModal;
